'use client'

import { useT } from '@repo/ui/hooks'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/table'
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  InitialTableState,
  RowSelectionState,
  SortingState as NativeSortingState,
  Table as TableType,
  useReactTable,
} from '@tanstack/react-table'
import { ReactNode, useEffect, useState } from 'react'

export type SortingState = NativeSortingState
interface DataTableProps<TData extends { id: string }, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  header?: (table: TableType<TData>) => ReactNode
  footer?: (table: TableType<TData>) => ReactNode
  initialState?: InitialTableState
  manualSorting?: boolean
  onSortingChange?: (sorting: SortingState) => void
}

export function DataTable<TData extends { id: string }, TValue>({
  columns,
  data,
  header,
  footer,
  initialState,
  manualSorting,
  onSortingChange,
}: DataTableProps<TData, TValue>) {
  const { tc } = useT()
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? [])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    initialState?.rowSelection ?? {},
  )
  useEffect(() => {
    onSortingChange?.(sorting)
  }, [sorting])

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState, // NOTE: we shouldn't be using both initialState and state (https://tanstack.com/table/latest/docs/framework/react/guide/table-state#custom-initial-state)
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    // Add getRowId to use the object's ID instead of row index
    getRowId: (row) => row.id,
    manualSorting: manualSorting ?? false,
    state: {
      columnFilters,
      rowSelection,
      sorting,
    },
  })

  return (
    <div className="relative">
      {header?.(table)}
      <div className="mb-4 rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {tc('noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {footer?.(table)}
    </div>
  )
}
